import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  POTENTIAL_CLIENT_ACTION_TYPES,
  POTENTIAL_CLIENT_REJECTED_STATUS,
  PotentialClient,
  PotentialClientActionType,
  PotentialClientOrigin,
  PotentialClientStatus,
} from 'src/app/models/potential-client.interface';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RingoverDialerService } from 'src/app/services/ringover-dialer.service';
import { PotentialClientCallService } from 'src/app/services/potential-client-call.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AddPotentialClientDialogComponent } from './add-potential-client-dialog/add-potential-client-dialog.component';
import { ImportPotentialClientsDialogComponent } from './import-potential-clients-dialog/import-potential-clients-dialog.component';
import { SyncMetaLeadsDialogComponent } from './sync-meta-leads-dialog/sync-meta-leads-dialog.component';
import { PotentialClientChangeStatusDialogComponent } from './potential-client-detail/potential-client-change-status-dialog/potential-client-change-status-dialog.component';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { EmployeeService } from 'src/app/services';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDateRangePicker } from '@angular/material/datepicker';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import dayjs from 'dayjs';

@Component({
  selector: 'app-potential-clients',
  templateUrl: './potential-clients.component.html',
  styleUrls: ['./potential-clients.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatSelect,
    MatOption,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    MatDateRangePicker,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    RouterLink,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    DatePipe,
    NgClass,
  ],
})
export class PotentialClientsComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private ringoverDialer = inject(RingoverDialerService);
  private potentialClientCall = inject(PotentialClientCallService);
  public permissions = inject(PermissionsService);
  public employee = inject(EmployeeService);
  protected override fb = inject(UntypedFormBuilder);
  protected override router = inject(Router);
  protected override activatedRoute = inject(ActivatedRoute);
  protected override utils = inject(UtilsService);

  displayedColumns: string[] = [
    'name',
    'phone',
    'email',
    'origin',
    'status',
    'actions_count',
    'last_action',
    'comercial',
    'meta_form_submitted_at',
    'updated_at',
    'actions',
  ];
  dataSource: MatTableDataSource<PotentialClient> = new MatTableDataSource<PotentialClient>();
  actionTypes = POTENTIAL_CLIENT_ACTION_TYPES;
  rejectedStatusSlug = POTENTIAL_CLIENT_REJECTED_STATUS;

  override filterForm: UntypedFormGroup;
  override sorting: any = { order_by: 'id:desc' };

  potentialClientStatuses: PotentialClientStatus[] = [];
  origins: PotentialClientOrigin[] = [];
  commercials: User[] = [];
  statusCounts: { slug: string; name: string; count: number }[] = [];
  statusCountsTotal = 0;

  private readonly defaultExcludedStatuses = new Set(['closed_won', 'closed_lost', 'qualified', 'rejected']);

  override filterFormExtraParams: any = {
    from: [this.parseOptionalDateQueryParam('from')],
    to: [this.parseOptionalDateQueryParam('to')],
    potential_client_status_slug: [[]],
    origin_id: [
      this.activatedRoute.snapshot.queryParamMap.get('origin_id') != null &&
      this.activatedRoute.snapshot.queryParamMap.get('origin_id') !== ''
        ? +this.activatedRoute.snapshot.queryParamMap.get('origin_id')!
        : '',
    ],
    commercial_id: [
      this.activatedRoute.snapshot.queryParamMap.get('commercial_id') != null &&
      this.activatedRoute.snapshot.queryParamMap.get('commercial_id') !== ''
        ? +this.activatedRoute.snapshot.queryParamMap.get('commercial_id')!
        : '',
    ],
  };

  override serializer: {
    from: () => string;
    to: () => string;
    potential_client_status_slug: () => string;
  } = {
    from: (): string => {
      const fromDate = dayjs(this.filterForm.value.from);
      return fromDate.isValid() ? fromDate.format('YYYY-MM-DD') : '';
    },
    to: (): string => {
      const toDate = dayjs(this.filterForm.value.to);
      return toDate.isValid() ? toDate.format('YYYY-MM-DD') : '';
    },
    potential_client_status_slug: (): string => {
      const value = this.filterForm?.get('potential_client_status_slug')?.value;
      if (!Array.isArray(value) || value.length === 0) {
        return '';
      }
      // Todos seleccionados = sin filtro en URL/API
      if (this.potentialClientStatuses.length > 0 && value.length === this.potentialClientStatuses.length) {
        return '';
      }
      return value.filter(Boolean).join(',');
    },
  };

  override deserializer: {
    potential_client_status_slug: (value: string | string[]) => string[];
  } = {
    potential_client_status_slug: (value: string | string[]): string[] => {
      // Angular serializa arrays como query params repetidos → llega string[].
      // Si aquí hacemos .split sobre un array, rompe listenQueryParameters y
      // formPersistence queda sin inicializar (crash en ngx-index-template al leer per_page).
      if (Array.isArray(value)) {
        return value.map(s => String(s).trim()).filter(Boolean);
      }
      if (!value || value === '') return [];
      return value
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    },
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadFilterOptions();
    if (this.ringoverDialer.isEnabledForCurrentUser()) {
      this.ringoverDialer.ensureInitialized();
    } else {
      this.ringoverDialer.destroy();
    }
  }

  get canUseRingover(): boolean {
    return this.ringoverDialer.isEnabledForCurrentUser();
  }

  callLead(item: PotentialClient): void {
    if (!item.phone || !this.canUseRingover) {
      return;
    }

    void this.potentialClientCall.callLeadAndProcess(item.id, item.phone, () => this.fetchData());
  }

  openEmail(item: PotentialClient): void {
    if (!item.email) {
      return;
    }
    window.location.href = `mailto:${item.email}`;
  }

  rejectAsDoesNotQualify(item: PotentialClient): void {
    if (item.potential_client_status_slug === POTENTIAL_CLIENT_REJECTED_STATUS) {
      return;
    }

    if (!confirm(`¿Rechazar a ${item.full_name} por «No califica»?`)) {
      return;
    }

    this.api
      .updatePotentialClient(String(item.id), {
        potential_client_status_slug: POTENTIAL_CLIENT_REJECTED_STATUS,
        rejection_reason: 'does_not_qualify',
      })
      .subscribe(() => {
        this.employee.refreshActionsRequired();
        this.fetchData();
      });
  }

  openChangeStatusDialog(item: PotentialClient): void {
    const dialogRef = this.dialog.open(PotentialClientChangeStatusDialogComponent, {
      width: '700px',
      data: {
        potential_client: item,
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data != null) {
        this.api.updatePotentialClient(String(item.id), data).subscribe(() => {
          this.employee.refreshActionsRequired();
          this.fetchData();
        });
      }
    });
  }

  enableMeetingMode() {
    this.employee.startMeetingMode();
  }

  disableMeetingMode() {
    this.employee.stopMeetingMode();
  }

  openAddPotentialClientDialog() {
    const dialogRef = this.dialog.open(AddPotentialClientDialogComponent, {
      width: '560px',
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data != null) {
        this.api.createPotentialClient(data).subscribe(() => {
          this.employee.refreshActionsRequired();
          this.fetchData();
        });
      }
    });
  }

  openImportPotentialClientsDialog() {
    const dialogRef = this.dialog.open(ImportPotentialClientsDialogComponent, {
      width: '560px',
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { imported: number; skipped_duplicates: number; skipped_invalid: number; total_rows: number } | null) => {
        if (result == null) return;

        alert(
          `Importación completada.\n` +
            `Importados: ${result.imported}\n` +
            `Duplicados omitidos: ${result.skipped_duplicates}\n` +
            `Inválidos omitidos: ${result.skipped_invalid}\n` +
            `Filas válidas leídas: ${result.total_rows}`
        );
        this.fetchData();
        this.employee.refreshActionsRequired();
      });
  }

  openSyncMetaLeadsDialog() {
    const dialogRef = this.dialog.open(SyncMetaLeadsDialogComponent, {
      width: '480px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != null) {
        this.fetchData();
        this.employee.refreshActionsRequired();
      }
    });
  }

  registerAction(item: PotentialClient, type: PotentialClientActionType) {
    const label = this.actionLabel(type);
    if (!confirm(`¿Registrar acción «${label}» para ${item.full_name}?`)) {
      return;
    }

    this.api.createPotentialClientAction(item.id, { type }).subscribe(() => {
      this.fetchData();
    });
  }

  actionLabel(type: string | undefined): string {
    if (!type) return '-';
    return this.actionTypes.find(a => a.value === type)?.label ?? type;
  }

  actionCount(item: PotentialClient, countKey: (typeof POTENTIAL_CLIENT_ACTION_TYPES)[number]['countKey']): number {
    return item[countKey] ?? 0;
  }

  lastAction(item: PotentialClient) {
    return item.potential_client_actions?.[0] ?? null;
  }

  getDetailQueryParams() {
    const value = { ...this.filterForm.value };
    // Guardar estados ya serializados (CSV) para que "Volver" restaure
    // query params compatibles con ngx-index-template / deserializer.
    if (Array.isArray(value.potential_client_status_slug)) {
      value.potential_client_status_slug = this.serializer.potential_client_status_slug();
    }
    if (value.from) {
      value.from = this.serializer.from();
    }
    if (value.to) {
      value.to = this.serializer.to();
    }
    return {
      b: JSON.stringify(value),
    };
  }

  /** Absolute path: mat-menu overlays resolve relative routerLinks from `/`. */
  getDetailRouterLink(item: PotentialClient): (string | number)[] {
    const root = this.permissions.isPartner() ? 'partners' : 'employees';
    return ['/', root, 'comercial', 'potential-clients', item.id];
  }

  clearPeriodFilter(): void {
    this.filterForm.patchValue({ from: null, to: null });
  }

  hasPeriodFilter(): boolean {
    return dayjs(this.filterForm?.value?.from).isValid() || dayjs(this.filterForm?.value?.to).isValid();
  }

  override fetchData() {
    const from = dayjs(this.filterForm.value.from);
    const to = dayjs(this.filterForm.value.to);
    const fromValid = from.isValid();
    const toValid = to.isValid();

    // Rango a medias: esperar a que el datepicker complete ambas fechas.
    if (fromValid !== toValid) {
      return;
    }

    const params = { ...this.filterForm.value };
    const statuses = params.potential_client_status_slug;
    if (
      Array.isArray(statuses) &&
      (statuses.length === 0 || (this.potentialClientStatuses.length > 0 && statuses.length === this.potentialClientStatuses.length))
    ) {
      params.potential_client_status_slug = '';
    } else if (Array.isArray(statuses)) {
      params.potential_client_status_slug = statuses.filter(Boolean).join(',');
    }

    // Sin fechas = Todos (sin filtro de periodo en API).
    params.from = fromValid ? from.format('YYYY-MM-DD') : '';
    params.to = toValid ? to.format('YYYY-MM-DD') : '';
    params.with = 'potential_client_status,commercial,origin';

    this.api.getPotentialClientsPaged(params).subscribe(data => {
      this.dataSource.data = data.data;
      this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
    });

    this.loadStatusCounts(params);
  }

  isStatusSelected(slug: string): boolean {
    const selected = this.filterForm?.get('potential_client_status_slug')?.value;
    return Array.isArray(selected) && selected.includes(slug);
  }

  isAllStatusesSelected(): boolean {
    const selected = this.filterForm?.get('potential_client_status_slug')?.value;
    if (!Array.isArray(selected) || this.potentialClientStatuses.length === 0) {
      return false;
    }

    return selected.length === this.potentialClientStatuses.length;
  }

  toggleStatusFilter(slug: string): void {
    const control = this.filterForm?.get('potential_client_status_slug');
    if (!control) {
      return;
    }

    const current: string[] = Array.isArray(control.value) ? [...control.value] : [];
    const index = current.indexOf(slug);

    // Already selected among several → keep only this one
    if (index >= 0 && current.length > 1) {
      control.setValue([slug]);
      return;
    }

    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(slug);
    }

    control.setValue(current.length > 0 ? current : this.defaultPipelineStatusSlugs());
  }

  selectAllStatuses(): void {
    this.filterForm?.get('potential_client_status_slug')?.setValue(this.potentialClientStatuses.map(status => status.slug));
  }

  private loadFilterOptions() {
    this.api.getPotentialClientStatus().subscribe(data => {
      this.potentialClientStatuses = Array.isArray(data) ? data : [];
      this.ensureDefaultStatusFilter();
    });
    this.api.getPotentialClientOrigins().subscribe(data => {
      this.origins = Array.isArray(data?.data) ? data.data : [];
    });
    this.api.getPotentialClientLeadAssignmentWeights().subscribe(data => {
      this.commercials = (Array.isArray(data?.data) ? data.data : []).map(
        row =>
          ({
            id: row.user_id,
            name: row.name,
            surnames: row.surnames,
          }) as User
      );
    });
  }

  private loadStatusCounts(listParams: Record<string, unknown>): void {
    const params = {
      search: listParams['search'] ?? '',
      origin_id: listParams['origin_id'] ?? '',
      commercial_id: listParams['commercial_id'] ?? '',
      from: listParams['from'] ?? '',
      to: listParams['to'] ?? '',
    };

    this.api.getPotentialClientStatusCounts(params).subscribe(data => {
      this.statusCounts = Array.isArray(data.statuses) ? data.statuses : [];
      this.statusCountsTotal = data.total ?? 0;
    });
  }

  private parseOptionalDateQueryParam(key: 'from' | 'to'): Date | null {
    const raw = this.activatedRoute.snapshot.queryParamMap.get(key);
    if (raw == null || raw === '') {
      return null;
    }

    const parsed = dayjs(raw, 'YYYY-MM-DD');
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private defaultPipelineStatusSlugs(): string[] {
    return this.potentialClientStatuses.filter(status => !this.defaultExcludedStatuses.has(status.slug)).map(status => status.slug);
  }

  private ensureDefaultStatusFilter() {
    const control = this.filterForm?.get('potential_client_status_slug');
    if (!control || this.potentialClientStatuses.length === 0) {
      return;
    }

    const current = control.value;
    if (!Array.isArray(current) || current.length === 0) {
      // emitEvent solo si formPersistence ya está listo (si no, ngx-index-template crashea).
      if (this.formPersistence == null) {
        control.setValue(this.defaultPipelineStatusSlugs(), { emitEvent: false });
        this.fetchData();
        return;
      }
      control.setValue(this.defaultPipelineStatusSlugs());
    }
  }
}
